import type { BackupEnvelope } from './backup.js';

const PBKDF2_ITERATIONS = 250_000;
const SALT_LEN = 16;
const IV_LEN = 12;

export interface EncryptedEnvelope {
  app: 'wean';
  formatVersion: 1;
  encrypted: true;
  kdf: 'PBKDF2-SHA256';
  iterations: number;
  salt: string;
  iv: string;
  cipherText: string;
  createdAt: string;
}

function b64encode(buf: ArrayBuffer | Uint8Array): string {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.byteLength; i++) bin += String.fromCharCode(bytes[i] ?? 0);
  return btoa(bin);
}
function b64decode(s: string): Uint8Array {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

async function deriveKey(passphrase: string, salt: Uint8Array): Promise<CryptoKey> {
  const baseKey = await crypto.subtle.importKey(
    'raw', new TextEncoder().encode(passphrase) as BufferSource,
    { name: 'PBKDF2' }, false, ['deriveKey']
  );
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: salt as BufferSource, iterations: PBKDF2_ITERATIONS, hash: 'SHA-256' },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

export async function encryptBackup(env: BackupEnvelope, passphrase: string): Promise<EncryptedEnvelope> {
  if (!passphrase) throw new Error('Passphrase required.');
  const salt = crypto.getRandomValues(new Uint8Array(SALT_LEN));
  const iv = crypto.getRandomValues(new Uint8Array(IV_LEN));
  const key = await deriveKey(passphrase, salt);
  const plaintext = new TextEncoder().encode(JSON.stringify(env));
  const cipher = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: iv as BufferSource }, key, plaintext as BufferSource);
  return {
    app: 'wean', formatVersion: 1, encrypted: true,
    kdf: 'PBKDF2-SHA256', iterations: PBKDF2_ITERATIONS,
    salt: b64encode(salt), iv: b64encode(iv),
    cipherText: b64encode(cipher),
    createdAt: env.createdAt,
  };
}

export async function decryptBackup(env: EncryptedEnvelope, passphrase: string): Promise<BackupEnvelope> {
  if (env.app !== 'wean' || !env.encrypted) throw new Error('Not an encrypted Wean backup.');
  const salt = b64decode(env.salt);
  const iv = b64decode(env.iv);
  const ct = b64decode(env.cipherText);
  const key = await deriveKey(passphrase, salt);
  let plain: ArrayBuffer;
  try {
    plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: iv as BufferSource }, key, ct as BufferSource);
  } catch {
    throw new Error('Wrong passphrase or corrupted file.');
  }
  const text = new TextDecoder().decode(plain);
  return JSON.parse(text) as BackupEnvelope;
}

export function isEncryptedFile(text: string): boolean {
  try {
    const o = JSON.parse(text);
    return o && o.app === 'wean' && o.encrypted === true;
  } catch { return false; }
}
